/**
 * Transactional filter drawer backed by notification facet metadata.
 */

import { useState } from "react";
import {
  CheckboxList,
  type CheckboxListItem,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { Button } from "risa-oasis-ui_v2";
import {
  countNotificationFilters,
  FILTER_LABELS,
  type NotificationFilters,
  type NotificationFacetOptions,
  type NotificationMultiSelectKey,
} from "../../../utils/notificationFilters";
import "./FilterControls.scss";

type FilterCategory = NotificationMultiSelectKey | "nextAppointment";

/** Inputs for the Patient Notification filter drawer. */
interface FilterControlsProps {
  activeFilters: NotificationFilters;
  onApply: (filters: NotificationFilters) => void;
  options: NotificationFacetOptions;
  appointmentBounds?: { min?: string; max?: string };
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  /**
   * Extra searchable strings per option value, keyed by category → value → aliases.
   * Used so the Diagnosis type-ahead also matches ICD codes (the option VALUE stays the
   * description the backend filters on). Absent categories match on label only.
   */
  optionAliases?: Partial<
    Record<NotificationMultiSelectKey, Record<string, string[]>>
  >;
}

/**
 * Render a two-pane filter drawer with Apply/Cancel draft semantics.
 *
 * @param props - Applied filters, dynamic facet options, and callbacks
 * @returns Filter trigger and drawer
 */
export function FilterControls({
  activeFilters,
  onApply,
  options,
  appointmentBounds,
  isLoading = false,
  error,
  onRetry,
  optionAliases,
}: FilterControlsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState<FilterCategory>("diagnosis");
  const [draft, setDraft] = useState<NotificationFilters>(activeFilters);
  // Type-ahead over the current category's option list (e.g. the long Diagnosis list).
  const [optionQuery, setOptionQuery] = useState("");

  const categories: FilterCategory[] = [
    "diagnosis",
    "stage",
    "insurance",
    "signoff",
    "patientTags",
    "nextAppointment",
  ];
  const invalidRange =
    !!draft.nextAppointment?.from &&
    !!draft.nextAppointment?.to &&
    draft.nextAppointment.from > draft.nextAppointment.to;

  /**
   * Open the drawer with a fresh copy of the applied filter state.
   *
   * @returns Nothing
   */
  const openDrawer = () => {
    setDraft(activeFilters);
    setCategory("diagnosis");
    setOptionQuery("");
    setIsOpen(true);
  };

  /**
   * Switch the active category and reset the option type-ahead.
   *
   * Deliberately does NOT pre-fill the appointment range with the full min–max bounds:
   * an unset range means "no appointment filter", so applying it only narrows when the
   * user actually picks dates (otherwise the filter was a silent no-op).
   *
   * @param nextCategory - Filter category selected in the drawer
   * @returns Nothing
   */
  const selectCategory = (nextCategory: FilterCategory) => {
    setCategory(nextCategory);
    setOptionQuery("");
  };

  /**
   * Merge a multi-select change into the draft, preserving selections that are
   * currently filtered out of view by the option search (so searching a second term
   * and selecting there never drops a pick made under a previous term).
   *
   * @param key - Filter dimension being updated
   * @param items - Checkbox list items (the currently VISIBLE subset) after the change
   * @returns Nothing
   */
  const updateMultiSelect = (
    key: NotificationMultiSelectKey,
    items: CheckboxListItem[],
  ) => {
    const selected = new Set(draft[key] ?? []);
    items.forEach((item) => {
      if (item.checked) selected.add(item.id);
      else selected.delete(item.id);
    });
    const next = Array.from(selected);
    setDraft({ ...draft, [key]: next.length ? next : undefined });
  };

  const categoryOptions =
    category === "nextAppointment" ? {} : (options[category] ?? {});
  const selectedValues =
    category === "nextAppointment" ? [] : (draft[category] ?? []);
  const listItems: CheckboxListItem[] = Array.from(
    new Set([...Object.keys(categoryOptions), ...selectedValues]),
  )
    .sort()
    .map((value) => ({
      id: value,
      label: `${value} (${categoryOptions[value] ?? 0})`,
      checked: selectedValues.includes(value),
    }));

  return (
    <div className="relative">
      <button
        type="button"
        onClick={openDrawer}
        className="inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium outline-none transition-colors focus:ring-2"
        style={{
          backgroundColor: "var(--color-input-bg)",
          borderColor: "var(--color-input-border)",
          color: "var(--color-text-primary)",
        }}
      >
        Filters ({countNotificationFilters(activeFilters)})
      </button>
      {isOpen && (
        <>
          <button
            type="button"
            aria-label="Close filters"
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setIsOpen(false)}
          />
          <section className="fixed right-0 top-0 z-50 flex h-full w-[800px] bg-[var(--color-card)] shadow-2xl">
            <aside className="w-[280px] border-r border-[var(--color-border)] bg-[var(--color-page-bg)]">
              <div className="flex items-center justify-between border-b border-[var(--color-border)] p-6">
                <h2 className="text-lg font-semibold">Filters</h2>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              {categories.map((item) => (
                <button
                  type="button"
                  key={item}
                  onClick={() => selectCategory(item)}
                  className={`w-full px-6 py-3 text-left text-sm ${
                    category === item ? "bg-[var(--color-card)]" : ""
                  }`}
                >
                  {FILTER_LABELS[item]}
                </button>
              ))}
            </aside>
            <div className="flex min-w-0 flex-1 flex-col">
              <header className="flex items-center justify-between border-b border-[var(--color-border)] p-6">
                <h3 className="font-medium">{FILTER_LABELS[category]}</h3>
                <button
                  type="button"
                  className="text-sm text-[var(--color-destructive)]"
                  onClick={() => setDraft({ ...draft, [category]: undefined })}
                >
                  Clear Selection
                </button>
              </header>
              <div className="ct-filter-panel flex-1 overflow-y-auto p-6">
                {isLoading && <p>Loading filter options…</p>}
                {!isLoading && error && (
                  <div>
                    <p>{error}</p>
                    {onRetry && (
                      <button type="button" onClick={onRetry}>
                        Retry
                      </button>
                    )}
                  </div>
                )}
                {!isLoading &&
                  !error &&
                  category !== "nextAppointment" &&
                  (listItems.length ? (
                    <div>
                      <input
                        type="search"
                        value={optionQuery}
                        onChange={(event) => setOptionQuery(event.target.value)}
                        placeholder={`Search ${FILTER_LABELS[category].toLowerCase()}…`}
                        aria-label={`Search ${FILTER_LABELS[category]} options`}
                        className="mb-3 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-input-bg)] px-3 py-2 text-sm"
                      />
                      {(() => {
                        const needle = optionQuery.trim().toLowerCase();
                        // `category` is narrowed to a multi-select key in this branch.
                        const aliases = optionAliases?.[category] ?? {};
                        const visibleItems = needle
                          ? listItems.filter(
                              (item) =>
                                item.label.toLowerCase().includes(needle) ||
                                (aliases[item.id] ?? []).some((alias) =>
                                  alias.toLowerCase().includes(needle),
                                ),
                            )
                          : listItems;
                        return visibleItems.length ? (
                          <div>
                            <div className="mb-2 flex items-center justify-end">
                              <button
                                type="button"
                                className="text-xs font-medium text-[var(--color-primary)]"
                                onClick={() =>
                                  updateMultiSelect(
                                    category,
                                    visibleItems.map((item) => ({
                                      ...item,
                                      checked: true,
                                    })),
                                  )
                                }
                              >
                                Select all ({visibleItems.length})
                              </button>
                            </div>
                            <CheckboxList
                              items={visibleItems}
                              onSelectionChange={(items: CheckboxListItem[]) =>
                                updateMultiSelect(category, items)
                              }
                              showSelectAll={false}
                            />
                          </div>
                        ) : (
                          <p>No matches for “{optionQuery}”.</p>
                        );
                      })()}
                    </div>
                  ) : (
                    <p>No options available for the selected date and tab.</p>
                  ))}
                {!isLoading && !error && category === "nextAppointment" && (
                  <div className="space-y-4">
                    {(["from", "to"] as const).map((field) => (
                      <label
                        key={field}
                        className="block text-sm font-medium capitalize"
                      >
                        {field}
                        <input
                          type="date"
                          value={draft.nextAppointment?.[field] ?? ""}
                          min={appointmentBounds?.min}
                          max={appointmentBounds?.max}
                          onChange={(event) =>
                            setDraft({
                              ...draft,
                              nextAppointment: {
                                ...draft.nextAppointment,
                                [field]: event.target.value || undefined,
                              },
                            })
                          }
                          className="mt-2 block w-full rounded-md border border-[var(--color-border)] bg-[var(--color-input-bg)] px-3 py-2"
                        />
                      </label>
                    ))}
                    {invalidRange && (
                      <p className="text-sm text-[var(--color-destructive)]">
                        From date must be on or before To date.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <footer className="flex justify-between border-t border-[var(--color-border)] p-6">
                <Button
                  buttonType="tertiary"
                  size="medium"
                  disabled={false}
                  onClick={() => setDraft({})}
                >
                  Clear All
                </Button>
                <div className="flex gap-3">
                  <Button
                    buttonType="secondary"
                    size="medium"
                    disabled={false}
                    onClick={() => setIsOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    buttonType="primary"
                    size="medium"
                    disabled={invalidRange}
                    onClick={() => {
                      onApply(draft);
                      setIsOpen(false);
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </footer>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
